/**
 * Lógica pura do carrinho do Ponto de Venda (sem Date, sem HTTP, sem Angular).
 * Dinheiro em centavos inteiros e quantidade em milésimos inteiros para evitar
 * erro de ponto flutuante (0,1 + 0,2). O servidor sempre prevalece sobre estes
 * cálculos (plano P5/P6/P10).
 *
 * Tipos do carrinho locais: `ProdutoPdv`/`PdvItem` (models/ponto-de-venda.model.ts)
 * usam `id`/`nomeProduto`, não `produtoId`/`descricao`, logo não são intercambiáveis;
 * o componente (T4) mapeia `ProdutoPdv` -> `ProdutoCarrinho` ao ler o código.
 */

export interface ProdutoCarrinho {
  produtoId: number;
  codigoBarra: string;
  descricao: string;
  unMedida: string | null;
  permiteFracionado: boolean;
  /** Preço unitário em reais, como vem do cadastro. */
  precoUnitario: number;
}

export interface ItemCarrinho extends ProdutoCarrinho {
  /** Quantidade (máx. 3 casas decimais). */
  quantidade: number;
}

export interface TotaisCarrinho {
  subtotalCentavos: number;
  descontoCentavos: number;
  totalCentavos: number;
}

export type ResultadoQuantidade =
  | { valida: true; quantidade: number }
  | { valida: false; motivo: string };

export const QUANTIDADE_MAXIMA = 9999.999;
const MILESIMOS = 1000;

const UNIDADES_FRACIONADAS = new Set(['KG', 'G', 'L', 'ML', 'M', 'M2', 'M3']);

/** Única regra de fracionamento: derivada da unidade de medida (UN, PC, CX, nulo e vazio são inteiros). */
export function permiteFracionadoPorUnidade(unMedida: string | null | undefined): boolean {
  return UNIDADES_FRACIONADAS.has((unMedida ?? '').trim().toUpperCase());
}

function paraMilesimos(valor: number): number {
  return Math.round(valor * MILESIMOS);
}

function quantidadeValida(quantidade: number, permiteFracionado: boolean): string | null {
  if (typeof quantidade !== 'number' || !Number.isFinite(quantidade)) return 'Quantidade inválida';
  if (quantidade <= 0) return 'Quantidade deve ser maior que zero';
  const milesimos = paraMilesimos(quantidade);
  if (milesimos > paraMilesimos(QUANTIDADE_MAXIMA)) return 'Quantidade acima do máximo permitido';
  if (Math.abs(quantidade * MILESIMOS - milesimos) > 1e-6) return 'Máximo de 3 casas decimais';
  if (!permiteFracionado && milesimos % MILESIMOS !== 0) {
    return 'Este produto aceita apenas quantidade inteira';
  }
  return null;
}

/** Agrupa por `produtoId`: soma a quantidade se a linha existir, senão inclui. */
export function adicionarOuSomar(
  itens: readonly ItemCarrinho[],
  produto: ProdutoCarrinho,
  quantidade = 1,
): ItemCarrinho[] {
  const existente = itens.find((i) => i.produtoId === produto.produtoId);
  if (existente) {
    const soma = (paraMilesimos(existente.quantidade) + paraMilesimos(quantidade)) / MILESIMOS;
    if (quantidadeValida(soma, existente.permiteFracionado) !== null) return [...itens];
    return itens.map((i) => (i.produtoId === produto.produtoId ? { ...i, quantidade: soma } : i));
  }
  if (quantidadeValida(quantidade, produto.permiteFracionado) !== null) return [...itens];
  return [...itens, { ...produto, quantidade: paraMilesimos(quantidade) / MILESIMOS }];
}

export function removerItem(itens: readonly ItemCarrinho[], produtoId: number): ItemCarrinho[] {
  return itens.filter((i) => i.produtoId !== produtoId);
}

/**
 * Valida e define a quantidade de uma linha (edição manual). Em caso de
 * rejeição, a lista é devolvida inalterada e `erro` traz o motivo.
 */
export function alterarQuantidade(
  itens: readonly ItemCarrinho[],
  produtoId: number,
  quantidade: number,
): { itens: ItemCarrinho[]; erro: string | null } {
  const item = itens.find((i) => i.produtoId === produtoId);
  if (!item) return { itens: [...itens], erro: 'Item não encontrado' };
  const erro = quantidadeValida(quantidade, item.permiteFracionado);
  if (erro) return { itens: [...itens], erro };
  const q = paraMilesimos(quantidade) / MILESIMOS;
  return { itens: itens.map((i) => (i.produtoId === produtoId ? { ...i, quantidade: q } : i)), erro: null };
}

/** Texto lido do leitor: apenas trim (sem multiplicador/prefixo de peso). */
export function normalizarLeitura(texto: string | null | undefined): string {
  return (texto ?? '').trim();
}

/**
 * Converte texto digitado em quantidade. Vírgula ou ponto como decimal, até 3
 * casas. Rejeita vazio, negativo, zero, NaN e fração em produto inteiro.
 */
export function parseQuantidade(
  texto: string | null | undefined,
  permiteFracionado: boolean,
): ResultadoQuantidade {
  const t = (texto ?? '').trim();
  if (!/^\d+([.,]\d+)?$/.test(t)) return { valida: false, motivo: 'Quantidade inválida' };
  const numero = Number(t.replace(',', '.'));
  const motivo = quantidadeValida(numero, permiteFracionado);
  if (motivo) return { valida: false, motivo };
  return { valida: true, quantidade: paraMilesimos(numero) / MILESIMOS };
}

/** Divisão inteira arredondando meio para cima (valores não negativos). */
function divisaoMeioParaCima(numerador: number, denominador: number): number {
  return Math.floor((2 * numerador + denominador) / (2 * denominador));
}

/** Total da linha em centavos (quantidade × preço, meio para cima). */
export function totalLinhaCentavos(item: ItemCarrinho): number {
  return divisaoMeioParaCima(Math.round(item.precoUnitario * 100) * paraMilesimos(item.quantidade), MILESIMOS);
}

/**
 * Subtotal, desconto (% sobre o subtotal, meio para cima) e total, tudo em
 * centavos inteiros. Percentual inválido (NaN, negativo, > 100) conta como 0.
 */
export function calcularTotais(
  itens: readonly ItemCarrinho[],
  descontoPercentual: number | null | undefined,
): TotaisCarrinho {
  const subtotalCentavos = itens.reduce((acc, i) => acc + totalLinhaCentavos(i), 0);
  const pct =
    typeof descontoPercentual === 'number' &&
    Number.isFinite(descontoPercentual) &&
    descontoPercentual > 0 &&
    descontoPercentual <= 100
      ? descontoPercentual
      : 0;
  const pontosBase = Math.round(pct * 100); // centésimos de ponto percentual
  const descontoCentavos = divisaoMeioParaCima(subtotalCentavos * pontosBase, 10000);
  return { subtotalCentavos, descontoCentavos, totalCentavos: subtotalCentavos - descontoCentavos };
}

/** Desconto válido: número finito, 0 <= pct <= max. */
export function descontoValido(percentual: number | null | undefined, maximo: number): boolean {
  if (typeof percentual !== 'number' || !Number.isFinite(percentual)) return false;
  if (!Number.isFinite(maximo)) return false;
  return percentual >= 0 && percentual <= maximo;
}
